import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Box,
  AppBar,
  Toolbar,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReactMarkdown from "react-markdown";
import { MapRainforestProduct, MapRainforestPhoto } from "types/map";
import { getProductIconSrc } from "libraries/map";
import MapMarkerRainforestVideoEmbed from "./MapMarkerRainforestVideoEmbed";

type Props = {
  productTag: string | null;
  productInfo?: MapRainforestProduct;
  onClose: () => void;
};

const MapMarkerRainforestInfoProductDialog = ({
  productTag,
  productInfo,
  onClose,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activePhoto, setActivePhoto] = useState<MapRainforestPhoto | null>(null);

  if (!productTag) return null;

  const iconUrl = getProductIconSrc(productTag);

  return (
    <Dialog
      open={!!productTag}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          boxShadow: isMobile ? "none" : "0 12px 40px rgba(0,0,0,0.3)",
        },
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#2e7d32",
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: isMobile ? 56 : 64, gap: 2 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {iconUrl ? (
              <Box
                component="img"
                src={iconUrl}
                alt={productTag}
                sx={{
                  width: 32,
                  height: 32,
                flexShrink: 0,
                  filter: "brightness(0) invert(1)", // Make it white
                }}
              />
            ) : (
              <CheckIcon sx={{ color: "#a5d6a7", fontSize: 28 }} />
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: "white",
                fontWeight: "bold",
                letterSpacing: 0.5,
                fontSize: isMobile ? "1.1rem" : "1.25rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {productTag}
            </Typography>
          </Stack>

          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ p: isMobile ? 2 : 4, bgcolor: "#ffffff" }}>
        {productInfo?.achievements && productInfo.achievements.length > 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "success.50",
              border: "1px solid",
              borderColor: "success.100",
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <EmojiEventsIcon sx={{ fontSize: 16, color: "success.500" }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: "success.800",
                  lineHeight: 1.2,
                }}
              >
                Achievements for {productTag}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {productInfo.achievements.map((achievement, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon
                    sx={{ fontSize: 15, color: "success.600", flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.82rem",
                      color: "success.900",
                      fontWeight: 500,
                    }}
                  >
                    {achievement.split('**').map((part, index) => 
                      index % 2 === 1 ? <strong key={index} style={{ fontWeight: 800 }}>{part}</strong> : part
                    )}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
        {/* Single legacy video if present */}
        {productInfo?.videoUrl && !productInfo.videos && (
          <Box sx={{ mb: 3 }}>
            <MapMarkerRainforestVideoEmbed url={productInfo.videoUrl} />
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<OpenInNewIcon />}
              href={productInfo.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                mt: 1,
                borderRadius: 0,
                textTransform: "none",
                fontWeight: 600,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "text.secondary",
                  bgcolor: "rgba(0,0,0,0.02)",
                },
              }}
            >
              Watch on YouTube
            </Button>
          </Box>
        )}

        {/* Product Videos */}
        {productInfo?.videos && productInfo.videos.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1.5, color: "success.800" }}>
              Documentaries & Videos
            </Typography>
            <Stack spacing={2}>
              {productInfo.videos.map((video, idx) => (
                <Box key={idx} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {video.title}
                  </Typography>
                  <MapMarkerRainforestVideoEmbed url={video.url} />
                  {video.description && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
                      {video.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Product Photos Gallery */}
        {productInfo?.photos && productInfo.photos.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1.5, color: "success.800" }}>
              Photo Gallery
            </Typography>
            <Grid container spacing={1}>
              {productInfo.photos.map((photo, idx) => (
                <Grid item xs={6} sm={4} key={idx}>
                  <Box
                    onClick={() => setActivePhoto(photo)}
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "75%", // 4:3 Ratio
                      borderRadius: 1.5,
                      overflow: "hidden",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        "& .photo-overlay": { opacity: 1 }
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.url}
                      alt={photo.title}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <Box
                      className="photo-overlay"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                        p: 1,
                        boxSizing: "border-box",
                        opacity: { xs: 1, sm: 0 },
                        transition: "opacity 0.2s ease-in-out",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.1,
                          fontSize: "0.7rem",
                        }}
                      >
                        {photo.title}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {productInfo ? (
          <Box
            sx={{
              "& h3": {
                fontSize: "1rem",
                fontWeight: 600,
                mt: 2,
                mb: 1,
                color: "text.primary",
              },
              "& h4": {
                fontSize: "0.9rem",
                fontWeight: 600,
                mt: 1.5,
                mb: 0.5,
                color: "text.primary",
              },
              "& p": {
                fontSize: "0.875rem",
                color: "text.secondary",
                lineHeight: 1.6,
                mb: 1.5,
              },
              "& ul": {
                pl: 2.5,
                mb: 1.5,
              },
              "& li": {
                fontSize: "0.875rem",
                color: "text.secondary",
                mb: 0.5,
              },
              "& strong": {
                color: "text.primary",
                fontWeight: 600,
              },
            }}
          >
            <ReactMarkdown>{productInfo.description}</ReactMarkdown>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No detailed information available for this product.
          </Typography>
        )}
      </DialogContent>

      {/* Photo Lightbox */}
      <Dialog
        open={!!activePhoto}
        onClose={() => setActivePhoto(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "black",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          }
        }}
      >
        <IconButton
          onClick={() => setActivePhoto(null)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        {activePhoto && (
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              src={activePhoto.url}
              alt={activePhoto.title}
              sx={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                display: "block",
                mx: "auto"
              }}
            />
            <Box sx={{ p: 2.5, bgcolor: "rgba(30,30,30,0.95)", color: "white" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "0.95rem", lineHeight: 1.4 }}>
                {activePhoto.title}
              </Typography>
              {activePhoto.credit && (
                <Typography variant="caption" sx={{ color: "grey.400", mt: 0.75, display: "block" }}>
                  Foto: {activePhoto.credit}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Dialog>
    </Dialog>
  );
};

export default MapMarkerRainforestInfoProductDialog;
